import {
  Box,
  Button,
  ButtonColor,
  ButtonPull,
  FormPasswordInput,
  formPasswordValidationSchema,
} from '@alirosoftware/web-components';
import React from 'react';
import { useTranslation } from 'next-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { ForgotPasswordResetInput, GetMeDocument, useForgotPasswordResetMutation } from '../../../graphql/_generated';
import { FormServerError } from '../../shared/forms/server-error';
import { HiOutlineLockClosed } from 'react-icons/hi';

type PasswordResetFormProps = {
  token: string;
  onReset: () => void;
};

export const PasswordResetForm = ({ token, onReset }: PasswordResetFormProps) => {
  const { t } = useTranslation();

  const [forgotPasswordReset, { error }] = useForgotPasswordResetMutation();

  const onSubmit = async (input: ForgotPasswordResetInput) => {
    await forgotPasswordReset({
      variables: { input },
      onCompleted: () => onReset(),
      refetchQueries: [GetMeDocument],
      awaitRefetchQueries: true,
    });
  };

  const validationSchema = Yup.object().shape({
    token: Yup.string().required(),
    password: formPasswordValidationSchema({
      t,
      minLength: 8,
      lowercase: true,
      uppercase: true,
      number: true,
    })
      .required()
      .label(t('newPassword')),
  });

  return (
    <>
      <h1 className="u-mb-4">{t('forgotPasswordResetForm')}</h1>
      <Box className="card card-vertical">
        <Formik<ForgotPasswordResetInput>
          onSubmit={onSubmit}
          initialValues={{ token, password: '' }}
          validationSchema={validationSchema}
          validateOnBlur={true}
          validateOnChange={false}
        >
          {({ handleSubmit, isSubmitting, submitForm }) => (
            <form onSubmit={handleSubmit}>
              <FormServerError error={error} />
              <FormPasswordInput
                name="password"
                placeholder={t('newPassword')}
                label={t('newPassword')}
                tabIndex={1}
                autoFocus
                icon={HiOutlineLockClosed}
              />

              <Box className="card-actions" mt={4}>
                <Button
                  color={ButtonColor.Primary}
                  onClick={submitForm}
                  loading={isSubmitting}
                  pull={ButtonPull.Right}
                  tabIndex={2}
                  testId="reset-password"
                >
                  {t('resetPassword')}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </>
  );
};
